import React, { useEffect, useMemo, useState } from 'react';
import { Container, Segment, Dimmer, Loader, Table, Button, Label, Form, Input, Icon, Dropdown, Modal, Message, Checkbox, Popup } from 'semantic-ui-react';
import { stockyApi } from '../misc/StockyApi';
import { useAuth } from '../context/AuthContext';
import { useStockInconsistencySummary } from '../context/StockInconsistencySummaryContext';
import { useItems } from '../../hooks/useItems';
import { useSyncControls } from '../../hooks/useSyncControls';
import BulkPriceUpdateModal from '../items/BulkPriceUpdateModal';
import BulkPricingImportWizardModal from '../items/BulkPricingImportWizardModal';
import BulkStockImportWizardModal from '../items/BulkStockImportWizardModal';
import SkuMigrationImportModal from '../items/SkuMigrationImportModal';
import ShowResultUpdateModal from '../items/ShowResultUpdateModal';
import ViewItemModal from '../items/ViewItemModal';
import SemanticUiReactSnackbar from '../misc/SemanticUiReactSnackbar';
import ItemsTable from '../items/Items';
import TableContentComponent from './TableContentComponent';
import PublishInAnotherAccountDrawer from '../stock/PublishInAnotherAccountDrawer';
import { shouldShowFirstCopyCallout } from '../stock/firstCopyOnboarding';
import { getPublishJobStatus } from '../stock/publishInAnotherAccountWorkflow';
import images from '../../images';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserFeatureFlags } from '../../hooks/useUserFeatureFlags';
import { formatCurrency } from '../misc/PriceFormatter';
import { loadListingFinancialBatches } from './financialBatches';
import { getSaleUnitQuantity } from './packStock';
import PackConfigModal from './PackConfigModal';
import { savePackModalResult } from './packConfigModalSave';
import KitCreateWizard from '../kits/KitCreateWizard';
import KitDetailModal from '../kits/KitDetailModal';
import './home.css';
import {
  pageBodyStyle,
  pageFrameStyle,
  pageShellStyle,
  sectionTitleStyle,
  surfaceCardStyle,
} from '../misc/pageStyles';

const ALL_FILTER_VALUE = '__all__';

const marketplaceOptions = [
  { key: 'all', text: 'Todos', value: ALL_FILTER_VALUE },
  { key: 'mercado_libre', text: 'Mercado Libre', value: 'mercado_libre' },
  { key: 'tienda_nube', text: 'Tienda Nube', value: 'tienda_nube' },
];

const statusOptions = [
  { key: 'all', text: 'Todos', value: ALL_FILTER_VALUE },
  { key: 'active', text: 'active', value: 'active' },
  { key: 'paused', text: 'paused', value: 'paused' },
  { key: 'closed', text: 'closed', value: 'closed' },
  { key: 'under_review', text: 'under_review', value: 'under_review' },
];

const marketplaceDisplayNames = {
  mercado_libre: 'Mercado Libre',
  tienda_nube: 'Tienda Nube',
};

const getExclusiveChannelShortLabel = (marketplace) => {
  if (marketplace === 'mercado_libre') {
    return 'Sin Tienda Nube';
  }
  if (marketplace === 'tienda_nube') {
    return 'Sin Mercado Libre';
  }
  return 'Elegí un canal primero';
};

const getExclusiveChannelHelp = (marketplace) => {
  if (marketplace === 'mercado_libre') {
    return 'Muestra publicaciones que están en Mercado Libre y todavía no existen en Tienda Nube.';
  }
  if (marketplace === 'tienda_nube') {
    return 'Muestra publicaciones que están en Tienda Nube y todavía no existen en Mercado Libre.';
  }
  return 'Elegí un canal para usar este filtro.';
};

const getFilterSelectValue = (value) => value || ALL_FILTER_VALUE;
const normalizeFilterSelectValue = (value) =>
  value === ALL_FILTER_VALUE ? '' : String(value || '');

const getSellerAccountType = (seller) =>
  String(seller?.accountType || seller?.account_type || '').trim().toLowerCase();

const buildSellerOptions = (sellers, marketplace) => {
  const matchingSellers = (Array.isArray(sellers) ? sellers : []).filter(
    (seller) => !marketplace || getSellerAccountType(seller) === marketplace,
  );

  return [
    { key: 'all', text: 'Todos los sellers', value: '' },
    ...matchingSellers.map((seller) => ({
      key: String(seller.id),
      value: String(seller.id),
      text: `${seller.nickname || seller.name || seller.email || seller.id} · ${
        marketplaceDisplayNames[getSellerAccountType(seller)] || 'Canal'
      }`,
    })),
  ];
};

const initialFilters = {
  search: '',
  sku: '',
  status: '',
  marketplace: '',
  sellerId: '',
  exclusiveToChannel: false,
};

const getHomeFilterSearch = (filters) => {
  const params = new URLSearchParams();
  const values = {
    search: String(filters.search || '').trim(),
    sku: String(filters.sku || '').trim(),
    status: String(filters.status || '').trim(),
    marketplace: String(filters.marketplace || '').trim(),
    sellerId: String(filters.sellerId || '').trim(),
    exclusiveToChannel: filters.exclusiveToChannel === true ? 'true' : '',
  };

  Object.entries(values).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });

  return params.toString();
};

const sectionCardTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
  fontWeight: 800,
  color: '#111827',
};

const toolbarCardStyle = {
  ...surfaceCardStyle,
  padding: '10px 12px',
  margin: 0,
  borderRadius: 10,
  boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
  height: '100%',
};

const denseSurfaceStyle = {
  ...surfaceCardStyle,
  padding: 10,
  borderRadius: 10,
  boxShadow: 'none',
};

const compactStatusRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};

const getActiveFilterCount = (filterValues) =>
  Object.values(filterValues || {}).filter(
    (value) => value === true || (typeof value === 'string' && value.trim().length > 0),
  ).length;

const homePageShellStyle = {
  ...pageShellStyle,
  maxWidth: 'none',
  width: '100%',
  margin: 0,
};

const homePageFrameStyle = {
  ...pageFrameStyle,
  borderRadius: 12,
  border: 'none',
  boxShadow: 'none',
  overflow: 'hidden',
  background: '#FFFFFF',
};

const homePageBodyStyle = {
  ...pageBodyStyle,
  padding: 0,
  background: 'transparent',
};

const GlobalProcesses = ({
  isPricesRoute,
  bulkPriceUpdateEnabled,
  skuMigrationImportEnabled,
  onStockImport,
  onSkuMigrationImport,
  onPricingImport,
}) => (
  <Segment style={toolbarCardStyle}>
    <div style={sectionCardTitleStyle}>
      <Icon name="upload" />
      Importaciones
    </div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
      {!isPricesRoute ? (
        <Button size="small" basic icon labelPosition="left" onClick={onStockImport}>
          <Icon name="upload" />
          Importar stock
        </Button>
      ) : null}
      {bulkPriceUpdateEnabled ? (
        <Button size="small" basic icon labelPosition="left" onClick={onPricingImport}>
          <Icon name="file alternate outline" />
          Importar costos y precios
        </Button>
      ) : null}
      {skuMigrationImportEnabled ? (
        <Button size="small" basic icon labelPosition="left" onClick={onSkuMigrationImport}>
          <Icon name="exchange" />
          Migrar SKU
        </Button>
      ) : null}
    </div>
  </Segment>
);

const SyncStatusPanel = ({
  userSyncEnabled,
  syncControlsLoading,
  onUserSyncToggle,
  canManage = true,
}) => (
  <Segment style={toolbarCardStyle}>
    <div style={compactStatusRowStyle}>
      <div>
        <div style={sectionCardTitleStyle}>
          <Icon name="refresh" />
          Sincronizacion de ventas
        </div>
        <Label basic color={userSyncEnabled ? 'green' : 'yellow'} style={{ marginTop: 10 }}>
          {userSyncEnabled ? 'Activa' : 'Pausada'}
        </Label>
      </div>
      {canManage ? (
        <Button size="small" basic loading={syncControlsLoading} onClick={onUserSyncToggle} style={{ alignSelf: 'center' }}>
          {userSyncEnabled ? 'Pausar' : 'Reanudar'}
        </Button>
      ) : (
        <span style={{ alignSelf: 'center', fontSize: 12.5, color: '#6B7280', maxWidth: 150 }}>
          La administra la cuenta dueña
        </span>
      )}
    </div>
  </Segment>
);

const BulkActionsBar = ({
  selectedCount,
  canPublishInAnotherAccount,
  packsEnabled,
  kitsEnabled,
  hasConfiguredKit,
  bulkPriceUpdateEnabled,
  bulkPriceSaving,
  bulkDeleteSaving,
  publishInAnotherAccountCopiesUsed,
  publishInAnotherAccountFreeLimit,
  showFirstCopyPill,
  onPublish,
  onFirstCopyStart,
  onPriceUpdate,
  onConfigurePack,
  onConfigureKit,
  onDelete,
  onClearSelection,
}) => {
  const hasSelection = selectedCount > 0;
  const hasExactlyOneSelection = selectedCount === 1;
  const kitSelectionHint = selectedCount === 0
    ? 'Selecciona una publicación para configurar su kit.'
    : selectedCount > 1
      ? 'Selecciona solo una publicación.'
      : '';
  const displayedCopyCount = showFirstCopyPill && hasSelection
    ? selectedCount
    : publishInAnotherAccountCopiesUsed;
  const actionDisabledStyle = !hasSelection
    ? { opacity: 0.55, cursor: 'not-allowed' }
    : undefined;

  return (
    <div
      aria-live="polite"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        padding: '10px 12px',
        marginBottom: 10,
        border: '1px solid #E5E7EB',
        borderRadius: 10,
        background: '#FFFFFF',
      }}
    >
      <div style={{ fontWeight: 800, color: '#1f2937' }}>
        Acciones
        <Label basic style={{ marginLeft: 8 }}>
          {selectedCount} seleccionadas
        </Label>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {canPublishInAnotherAccount ? (
          <Button
            size="small"
            basic
            icon
            labelPosition="left"
            className={showFirstCopyPill ? 'first-copy-publish-action' : undefined}
            onClick={() => (showFirstCopyPill && !hasSelection ? onFirstCopyStart() : onPublish())}
            aria-disabled={!hasSelection}
            style={showFirstCopyPill ? { opacity: 1, cursor: 'pointer' } : actionDisabledStyle}
          >
            <Icon name="send" />
            Publicar en otra cuenta
            {showFirstCopyPill ? (
              <span className="first-copy-publish-action__new-badge">
                <span
                  aria-hidden="true"
                  className="first-copy-publish-action__new-dot"
                />
                Nuevo
              </span>
            ) : null}
            {publishInAnotherAccountFreeLimit > 0 ? (
              <span style={{ marginLeft: 8, opacity: 0.8, fontSize: 12 }}>
                {displayedCopyCount}/{publishInAnotherAccountFreeLimit}
              </span>
            ) : null}
          </Button>
        ) : null}
        {bulkPriceUpdateEnabled ? (
          <Button
            size="small"
            basic
            icon
            labelPosition="left"
            onClick={onPriceUpdate}
            loading={bulkPriceSaving}
            disabled={bulkPriceSaving || bulkDeleteSaving}
            aria-disabled={!hasSelection || bulkPriceSaving || bulkDeleteSaving}
            style={actionDisabledStyle}
          >
            <Icon name="sync alternate" />
            Actualizar precios
          </Button>
        ) : null}
        {packsEnabled ? (
          <Button
            size="small"
            basic
            color="violet"
            icon
            labelPosition="left"
            onClick={onConfigurePack}
            disabled={bulkPriceSaving || bulkDeleteSaving}
            aria-disabled={!hasSelection || bulkPriceSaving || bulkDeleteSaving}
            style={actionDisabledStyle}
          >
            <Icon name="cubes" />
            Configurar pack
          </Button>
        ) : null}
        {kitsEnabled ? (
          <Popup
            content={kitSelectionHint}
            disabled={!kitSelectionHint}
            position="top center"
            trigger={(
              <span>
                <Button
                  size="small"
                  basic
                  color="violet"
                  icon
                  labelPosition="left"
                  className="kit-create-action"
                  onClick={onConfigureKit}
                  disabled={!hasExactlyOneSelection || bulkPriceSaving || bulkDeleteSaving}
                  aria-disabled={!hasExactlyOneSelection || bulkPriceSaving || bulkDeleteSaving}
                  style={!hasExactlyOneSelection ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                >
                  <Icon name="cubes" />
                  Configurar kit
                  {!hasConfiguredKit ? <span className="first-copy-publish-action__new-badge">
                    <span aria-hidden="true" className="first-copy-publish-action__new-dot" />
                    Nuevo
                  </span> : null}
                </Button>
              </span>
            )}
          />
        ) : null}
        <Button
          size="small"
          basic
          color="red"
          icon
          labelPosition="left"
          onClick={onDelete}
          loading={bulkDeleteSaving}
          disabled={bulkPriceSaving || bulkDeleteSaving}
          aria-disabled={!hasSelection || bulkPriceSaving || bulkDeleteSaving}
          style={actionDisabledStyle}
        >
          <Icon name="trash" />
          Eliminar
        </Button>
        <Button size="small" basic onClick={onClearSelection} disabled={!hasSelection || bulkPriceSaving || bulkDeleteSaving}>
          Cancelar seleccion
        </Button>
      </div>
    </div>
  );
};

const formatFinancialCurrency = (value) => {
  return formatCurrency(value, { currency: 'ARS' });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const resolveFinancialStatusColor = (status) => {
  if (status === 'READY') return 'green';
  if (status === 'STALE' || status === 'PARTIAL') return 'orange';
  if (status === 'UNAUTHORIZED' || status === 'ERROR' || status === 'FAILED') return 'red';
  return undefined;
};

const hasInternalCostLoaded = (financial) => financial?.hasInternalCost === true;

const renderMarginText = (financial) => {
  if (!financial) return '-';
  if (!hasInternalCostLoaded(financial)) return 'Sin costo interno';
  return formatFinancialCurrency(financial.marginAmount);
};

const formatMarginPercent = (financial) => {
  if (!financial || !hasInternalCostLoaded(financial)) {
    return '-';
  }
  const costAmount = Number(financial.costAmount || 0);
  if (!Number.isFinite(costAmount) || costAmount <= 0) {
    return '-';
  }
  const percent = (Number(financial.marginAmount || 0) / costAmount) * 100;
  if (!Number.isFinite(percent)) {
    return '-';
  }
  return `${percent.toFixed(2)}%`;
};

const buildListingFinancialMap = (rows) => {
  const next = {};
  (rows || []).forEach((row) => {
    if (row?.listingId) {
      next[row.listingId] = row;
    }
  });
  return next;
};

const extractApiErrorData = (error) => error?.response?.data || {};
const getSkuConfirmationCode = (error) => extractApiErrorData(error)?.code || '';
const isInlineConfirmationError = (error) =>
  [
    'SHARED_ITEM_SPLIT_REQUIRES_CONFIRMATION',
    'EXISTING_SKU_REBIND_REQUIRES_CONFIRMATION',
    'ML_VARIATION_PRICE_CHANGE_REQUIRES_CONFIRMATION',
  ].includes(getSkuConfirmationCode(error));

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicationGridRoute = ['/home', '/stock', '/publicaciones'].includes(location.pathname);
  const isPricesRoute = location.pathname === '/precios';
  const Auth = useAuth();
  const { refreshSummary: refreshStockInconsistencySummary } = useStockInconsistencySummary();
  const accessToken = Auth.user?.accessToken || Auth.getUser()?.accessToken || '';
  const authUser = useMemo(() => (accessToken ? { accessToken } : null), [accessToken]);
  const isAdmin = Auth.isAdmin();
  const {
    loading: featureFlagsLoading,
    packsEnabled,
    kitsEnabled,
    skuMigrationImportEnabled,
    publishInAnotherAccountEnabled,
    publishInAnotherAccountCopiesUsed,
    publishInAnotherAccountFreeLimit,
    refresh: refreshUserFeatureFlags,
  } = useUserFeatureFlags(authUser);
  const canPublishInAnotherAccount = publishInAnotherAccountEnabled === true;

  const [selectedItem, setSelectedItem] = useState(null);
  const [bulkPriceUpdateModalOpen, setBulkPriceUpdateModalOpen] = useState(false);
  const [bulkPricingImportModalOpen, setBulkPricingImportModalOpen] = useState(false);
  const [bulkStockImportModalOpen, setBulkStockImportModalOpen] = useState(false);
  const [skuMigrationImportModalOpen, setSkuMigrationImportModalOpen] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [bulkPriceUpdateFormData, setBulkPriceUpdateFormData] = useState({ priceType: 'percentage', increaseAmount: '' });
  const [bulkPriceSaving, setBulkPriceSaving] = useState(false);
  const [bulkDeleteSaving, setBulkDeleteSaving] = useState(false);
  const [bulkPackModalOpen, setBulkPackModalOpen] = useState(false);
  const [kitCreateWizardOpen, setKitCreateWizardOpen] = useState(false);
  const [kitSourceListing, setKitSourceListing] = useState(null);
  const [kitDetailId, setKitDetailId] = useState(null);
  const [hasCreatedKit, setHasCreatedKit] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [publishSelectionMode, setPublishSelectionMode] = useState(false);
  const [copiedSourceListingIds, setCopiedSourceListingIds] = useState([]);
  const [publishDrawerOpen, setPublishDrawerOpen] = useState(false);
  const [publishJobId, setPublishJobId] = useState(null);
  const [publishJobSnapshot, setPublishJobSnapshot] = useState(null);
  const [publishToast, setPublishToast] = useState(null);
  const [publishToastVisible, setPublishToastVisible] = useState(false);
  const [publishToastJobId, setPublishToastJobId] = useState(null);
  const [firstCopyGuidanceActive, setFirstCopyGuidanceActive] = useState(false);
  const [firstCopyDestinationAccounts, setFirstCopyDestinationAccounts] = useState(null);
  const [firstCopyDestinationAccountsLoading, setFirstCopyDestinationAccountsLoading] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [column, setColumn] = useState(null);
  const [direction, setDirection] = useState(null);
  const [pendingFilters, setPendingFilters] = useState(initialFilters);
  const [showMessage, setShowMessage] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarVariant, setSnackbarVariant] = useState('success');
  const [updateResults, setUpdateResults] = useState([]);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [rowEditEnabled, setRowEditEnabled] = useState({});
  const [listingFinancialsById, setListingFinancialsById] = useState({});
  const [listingFinancialsLoadingById, setListingFinancialsLoadingById] = useState({});
  const [selectedFinancialDetail, setSelectedFinancialDetail] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [financialRefreshNonce, setFinancialRefreshNonce] = useState(0);
  const financialRequestRef = React.useRef(null);
  const listingFinancialsByIdRef = React.useRef({});
  const publicationSelectionRef = React.useRef(null);

  const {
    items,
    setItems,
    isLoading,
    setIsLoading,
    error: itemsError,
    filters,
    setFilters,
    page,
    setPage,
    pageSize,
    setPageSize,
    total,
    fetchItems,
  } = useItems(authUser, initialFilters);

  const {
    userSyncEnabled,
    bulkPriceUpdateEnabled,
    syncControlsLoading,
    canManageSync,
    toggleUserSync,
  } = useSyncControls(authUser, isAdmin);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const isCatalogListing = (item) => {
    const listing = item?._raw?.listing || {};
    return listing.catalogListing === true || listing.catalog_listing === true;
  };

  const atomicItems = useMemo(() => {
    const grouped = new Map();

    (items || []).forEach((item) => {
      const normalizedSku = String(item?.sku || '').trim().toLowerCase();
      const skuKey = normalizedSku || `NO-SKU-${item?.id}`;
      const isCatalog = isCatalogListing(item);
      if (!grouped.has(skuKey)) {
        const mainLink = item?.permalink || item?._raw?.listing?.link || item?._raw?.listing?.permalink || '';
        grouped.set(skuKey, {
          id: item.id,
          title: item.title || 'Sin titulo',
          variationName: item.variationName || '',
          sku: item.sku || '',
          kind: item.kind || 'SIMPLE',
          availableQuantity: Number(item.availableQuantity || 0),
          cost: item.cost || '0.00',
          price: Number(item.price || 0),
          status: item.status || 'active',
          sellerNickname: item.sellerNickname || '-',
          marketplace: item.marketplace || '',
          permalink: mainLink,
          pictureUrl: item.pictureUrl || '',
          primaryListingData: item,
          listings: [],
          hasCatalogListing: false,
        });
      }

      const row = grouped.get(skuKey);
      row.availableQuantity = Math.max(row.availableQuantity, Number(item.availableQuantity || 0));
      row.price = Math.max(Number(row.price || 0), Number(item.price || 0));
      row.hasCatalogListing = row.hasCatalogListing || isCatalog;
      row.listings.push({
        id: item.listingId || `${item.marketplace}-${item.marketplaceId}-${item.id}`,
        itemId: item.id,
        kind: item.kind || 'SIMPLE',
        sourceItem: item,
        marketplace: item.marketplace,
        sellerNickname: item.sellerNickname || '-',
        permalink: item.permalink || item?._raw?.listing?.link || item?._raw?.listing?.permalink || '',
        status: item.status || '-',
        statusSubStatuses: Array.isArray(item.statusSubStatuses) ? item.statusSubStatuses : [],
        price: Number(item.price || 0),
        sku: item.sku || '',
        lastComputedAt: item.lastComputedAt || null,
        availableQuantity: Number(item.availableQuantity || 0),
        sellerStockQuantity: Number(item.sellerStockQuantity ?? item.availableQuantity ?? 0),
        saleUnitQuantity: getSaleUnitQuantity(item.saleUnitQuantity),
        reportableStock:
          item.reportableStock !== null &&
          item.reportableStock !== undefined &&
          Number.isFinite(Number(item.reportableStock))
            ? Math.max(0, Math.floor(Number(item.reportableStock)))
            : undefined,
        marketplaceStockQuantity:
          item.marketplaceStockQuantity !== null &&
          item.marketplaceStockQuantity !== undefined &&
          Number.isFinite(Number(item.marketplaceStockQuantity))
            ? Math.max(0, Math.floor(Number(item.marketplaceStockQuantity)))
            : null,
        fullStockQuantity: Number(item.fullStockQuantity || 0),
        stockMode: item.stockMode || 'standard',
        stockEditable: item.stockEditable !== false,
        title: item.title || '-',
        pictureUrl: item.pictureUrl || '',
        marketplaceId: item.marketplaceId || '',
        isCatalog,
      });
    });

    return Array.from(grouped.values());
  }, [items]);

  const filteredItems = atomicItems;
  const hasConfiguredKit = hasCreatedKit || (items || []).some((item) => item.kind === 'KIT');
  const visibleItemIds = useMemo(
    () => (filteredItems || []).map((item) => String(item.id || '').trim()).filter(Boolean),
    [filteredItems],
  );
  const visibleListingIds = useMemo(
    () =>
      (filteredItems || []).flatMap((item) =>
        (item.listings || [])
          .map((listing) => String(listing.id || '').trim())
          .filter(Boolean),
      ),
    [filteredItems],
  );
  const selectedVisibleListingIds = useMemo(() => {
    const visibleSet = new Set(visibleListingIds);
    return (selectedListingIds || [])
      .map((value) => String(value || '').trim())
      .filter((value) => value && visibleSet.has(value));
  }, [selectedListingIds, visibleListingIds]);
  const allVisibleListingsSelected =
    visibleListingIds.length > 0 &&
    visibleListingIds.every((listingId) => selectedVisibleListingIds.includes(listingId));
  const someVisibleListingsSelected =
    selectedVisibleListingIds.length > 0 && !allVisibleListingsSelected;
  const selectedBulkPriceRows = useMemo(
    () => filteredItems.filter((item) => selectedItems.includes(item.id)),
    [filteredItems, selectedItems],
  );
  const selectedListingRows = useMemo(() => {
    const selectedIds = new Set(selectedListingIds.map((value) => String(value || '').trim()).filter(Boolean));
    if (selectedIds.size === 0) {
      return [];
    }

    return filteredItems.flatMap((item) =>
      (item.listings || [])
        .filter((listing) => selectedIds.has(String(listing.id || '').trim()))
        .map((listing) => ({
          id: String(listing.id || '').trim(),
          sku: item.sku || listing.sku || '',
          title: listing.title || item.title || 'Sin titulo',
          marketplace: listing.marketplace,
          sellerNickname: listing.sellerNickname || item.sellerNickname || '-',
          permalink: listing.permalink || '',
          status: listing.status || item.status || 'active',
          listings: [listing],
          primaryListingData: listing.sourceItem || item.primaryListingData || item,
          marketplaceId: listing.marketplaceId || '',
          isCatalog: listing.isCatalog === true,
          kind: listing.kind || item.kind || 'SIMPLE',
          rowId: item.id,
        })),
    );
  }, [filteredItems, selectedListingIds]);
  const selectedPackListings = useMemo(
    () =>
      selectedListingRows
        .map((row) => row?.listings?.[0])
        .filter((listing) => Boolean(listing?.id)),
    [selectedListingRows],
  );
  const packEligibleListings = useMemo(
    () =>
      selectedPackListings.filter(
        (listing) =>
          !(
            listing.marketplace === 'mercado_libre' &&
            String(listing.stockMode || 'standard') === 'full_only'
          ),
      ),
    [selectedPackListings],
  );
  const selectedPublishRows = selectedListingRows;
  const selectedRowsForPriceUpdate =
    selectedListingRows.length > 0 ? selectedListingRows : selectedBulkPriceRows;
  const shouldCheckFirstCopyDestination =
    isPublicationGridRoute &&
    !featureFlagsLoading &&
    canPublishInAnotherAccount &&
    publishInAnotherAccountCopiesUsed === 0 &&
    publishInAnotherAccountFreeLimit === 5;
  const showFirstCopyCallout = shouldShowFirstCopyCallout({
    featureFlagsLoading,
    enabled: canPublishInAnotherAccount,
    copiesUsed: publishInAnotherAccountCopiesUsed,
    freeLimit: publishInAnotherAccountFreeLimit,
    isPublicationsRoute: isPublicationGridRoute,
  });

  const sellerOptions = useMemo(
    () => buildSellerOptions(sellers, pendingFilters.marketplace),
    [pendingFilters.marketplace, sellers],
  );

  useEffect(() => {
    if (!authUser) {
      setSellers([]);
      setSellersLoading(false);
      return undefined;
    }

    let alive = true;
    setSellersLoading(true);
    stockyApi
      .getSellers(authUser)
      .then((response) => {
        if (alive) {
          setSellers(Array.isArray(response?.data) ? response.data : []);
        }
      })
      .catch(() => {
        if (alive) {
          setSellers([]);
        }
      })
      .finally(() => {
        if (alive) {
          setSellersLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [authUser]);

  useEffect(() => {
    const visibleListingIdSet = new Set(visibleListingIds);
    const visibleItemIdSet = new Set(visibleItemIds);

    setSelectedListingIds((prev) =>
      (prev || []).filter((listingId) => visibleListingIdSet.has(String(listingId || '').trim())),
    );
    setSelectedItems((prev) =>
      (prev || []).filter((itemId) => visibleItemIdSet.has(String(itemId || '').trim())),
    );
  }, [visibleListingIds, visibleItemIds]);

  useEffect(() => {
    if (!shouldCheckFirstCopyDestination) {
      setFirstCopyDestinationAccounts(null);
      setFirstCopyDestinationAccountsLoading(false);
      return undefined;
    }

    setFirstCopyDestinationAccounts(sellers);
    setFirstCopyDestinationAccountsLoading(sellersLoading);
    return undefined;
  }, [sellers, sellersLoading, shouldCheckFirstCopyDestination]);
  const selectedDeleteChannelsLabel = useMemo(() => {
    const channels = Array.from(
      new Set(
        selectedListingRows
          .map((listing) => marketplaceDisplayNames[listing.marketplace] || listing.marketplace)
          .filter(Boolean),
      ),
    );
    if (channels.length === 0) return 'los canales';
    if (channels.length === 1) return channels[0];
    return channels.join(', ');
  }, [selectedListingRows]);
  const mlListingIds = useMemo(
    () =>
      Array.from(
        new Set(
          filteredItems.flatMap((item) =>
            (item.listings || [])
              .filter((listing) => listing.marketplace === 'mercado_libre')
              .map((listing) => String(listing.id || '').trim())
              .filter(Boolean),
          ),
        ),
      ).sort(),
    [filteredItems],
  );

  const listingFinancialViewKey = useMemo(
    () => [
      isPricesRoute ? 'prices' : 'other', page, pageSize, filters.search, filters.sku,
      filters.status, filters.marketplace, filters.sellerId, filters.exclusiveToChannel,
      mlListingIds.join(','), financialRefreshNonce,
    ].join('|'),
    [filters.exclusiveToChannel, filters.marketplace, filters.search, filters.sellerId, filters.sku, filters.status, financialRefreshNonce, isPricesRoute, mlListingIds, page, pageSize],
  );

  useEffect(() => {
    if (!isPricesRoute || !authUser || bulkPriceUpdateEnabled !== true || mlListingIds.length === 0) {
      return undefined;
    }
    financialRequestRef.current?.controller?.abort();
    const controller = new AbortController();
    const request = { key: listingFinancialViewKey, controller };
    financialRequestRef.current = request;
    const isCurrent = () => financialRequestRef.current === request && !controller.signal.aborted;

    const applyRows = (rows) => {
      if (isCurrent()) {
        setListingFinancialsById((previous) => {
          const next = { ...previous, ...buildListingFinancialMap(rows) };
          listingFinancialsByIdRef.current = next;
          return next;
        });
      }
    };
    const setBatchLoading = (listingIds, isLoading) => {
      setListingFinancialsLoadingById((previous) => {
        const next = { ...previous };
        listingIds.forEach((listingId) => {
          if (isLoading) {
            next[listingId] = request;
          } else if (next[listingId] === request) {
            delete next[listingId];
          }
        });
        return next;
      });
    };

    void loadListingFinancialBatches({
      listingIds: mlListingIds,
      getSnapshots: (listingIds) => stockyApi.getListingFinancials(
        listingIds, authUser, { signal: controller.signal },
      ),
      refreshSnapshots: (listingIds) => stockyApi.refreshListingFinancials(
        { listingIds }, authUser, { signal: controller.signal },
      ),
      isCurrent,
      onRows: applyRows,
      onLoading: setBatchLoading,
      getCurrentRow: (listingId) => listingFinancialsByIdRef.current[listingId],
    });
    return () => {
      controller.abort();
      setBatchLoading(mlListingIds, false);
    };
  // `listingFinancialViewKey` is the semantic dependency. Do not depend on the array identity.
  }, [authUser, bulkPriceUpdateEnabled, isPricesRoute, listingFinancialViewKey]);

  const applyFilters = () => {
    const sku = (pendingFilters.sku || '').trim();
    const normalizedSku = sku.length >= 3 ? sku : '';
    const nextFilters = {
      ...pendingFilters,
      sku: normalizedSku,
      search: (pendingFilters.search || '').trim(),
      sellerId: (pendingFilters.sellerId || '').trim(),
      exclusiveToChannel:
        pendingFilters.marketplace !== '' && pendingFilters.exclusiveToChannel === true,
    };

    setPendingFilters(nextFilters);
    setFilters(nextFilters);
    setPage(1);

    if (isPublicationGridRoute) {
      const search = getHomeFilterSearch(nextFilters);
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    }
  };

  const clearFilters = () => {
    setPendingFilters(initialFilters);
    setFilters(initialFilters);
    setPage(1);

    if (isPublicationGridRoute) {
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
    }
  };

  const handleFiltersSubmit = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const handleSelectAll = React.useCallback(() => {
    const visibleListingIdSet = new Set(visibleListingIds);
    const visibleItemIdSet = new Set(visibleItemIds);

    if (allVisibleListingsSelected) {
      setSelectedItems((prev) =>
        (prev || []).filter((itemId) => !visibleItemIdSet.has(String(itemId || '').trim())),
      );
      setSelectedListingIds((prev) =>
        (prev || []).filter((listingId) => !visibleListingIdSet.has(String(listingId || '').trim())),
      );
    } else {
      setSelectedItems((prev) => Array.from(new Set([...(prev || []), ...visibleItemIds])));
      setSelectedListingIds((prev) => Array.from(new Set([...(prev || []), ...visibleListingIds])));
    }
  }, [allVisibleListingsSelected, visibleItemIds, visibleListingIds]);

  const handleClearSelection = () => {
    setSelectedItems([]);
    setSelectedListingIds([]);
    setPublishSelectionMode(false);
  };

  const handleEditClick = React.useCallback((item) => {
    setRowEditEnabled((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
  }, []);

  const handleViewClick = React.useCallback((item) => {
    const source = item?.sourceItem || item?.primaryListingData || item;
    setSelectedItem(source);
    setViewModalOpen(true);
  }, []);

  const handleBulkPriceUpdateModalOpen = () => setBulkPriceUpdateModalOpen(true);
  const handleBulkPriceUpdateModalClose = () => setBulkPriceUpdateModalOpen(false);
  const handleBulkPricingImportModalOpen = () => setBulkPricingImportModalOpen(true);
  const handleBulkPricingImportModalClose = () => setBulkPricingImportModalOpen(false);
  const handleBulkStockImportModalOpen = () => setBulkStockImportModalOpen(true);
  const handleBulkStockImportModalClose = () => setBulkStockImportModalOpen(false);
  const handleSkuMigrationImportModalOpen = () => setSkuMigrationImportModalOpen(true);
  const handleSkuMigrationImportModalClose = () => setSkuMigrationImportModalOpen(false);
  const handleBulkDeleteButtonClick = () => {
    if (selectedListingRows.length === 0) {
      setSnackbarVariant('info');
      setSnackbarMessage('Selecciona al menos una publicación para usar esta acción.');
      setShowMessage(true);
      return;
    }
    setBulkDeleteModalOpen(true);
  };
  const handlePublishDrawerOpen = () => {
    const hasSelectedListings = selectedListingIds
      .map((value) => String(value || '').trim())
      .some(Boolean);

    setCopiedSourceListingIds([]);

    if (!publishSelectionMode && !hasSelectedListings) {
      setPublishSelectionMode(true);
      setSnackbarVariant('info');
      setSnackbarMessage('Selecciona las publicaciones que quieres publicar en otra cuenta.');
      setShowMessage(true);
      return;
    }

    if (!hasSelectedListings) {
      setSnackbarVariant('info');
      setSnackbarMessage('Selecciona al menos una publicación para usar esta acción.');
      setShowMessage(true);
      return;
    }

    if (!publishSelectionMode) {
      setPublishSelectionMode(true);
    }
    setPublishDrawerOpen(true);
  };

  const handleFirstCopyStart = () => {
    setCopiedSourceListingIds([]);
    setFirstCopyGuidanceActive(true);
  };

  const handleFirstCopyGuidanceDismiss = () => {
    setFirstCopyGuidanceActive(false);
  };

  useEffect(() => {
    const hasSelectedListing = selectedListingIds.some((value) => String(value || '').trim());
    if (!firstCopyGuidanceActive || !hasSelectedListing) {
      return;
    }

    setFirstCopyGuidanceActive(false);
    setPublishDrawerOpen(true);
  }, [firstCopyGuidanceActive, selectedListingIds]);

  const handlePublishDrawerClose = async () => {
    const jobStatus = String(publishJobSnapshot?.status || '').toUpperCase();
    const createdCount = Number(publishJobSnapshot?.summary?.created || 0);
    const shouldRefreshItems =
      (jobStatus === 'COMPLETED' || jobStatus === 'COMPLETED_WITH_ERRORS') && createdCount > 0;

    if (shouldRefreshItems) {
      // Al terminar la copia se sale del modo selección, que abre todas las filas,
      // y quedan expandidos sólo los SKUs que se copiaron.
      const copiedSourceListingIds = Array.from(
        new Set(
          (publishJobSnapshot?.results || [])
            .filter((result) => result?.status === 'created')
            .flatMap((result) =>
              Array.isArray(result?.sourceListingIds) && result.sourceListingIds.length > 0
                ? result.sourceListingIds
                : [result?.rowId],
            )
            .map((value) => String(value || '').trim())
            .filter(Boolean),
        ),
      );

      setCopiedSourceListingIds(copiedSourceListingIds);
      setPublishSelectionMode(false);
      setSelectedListingIds([]);

      if (page === 1) {
        await fetchItems({ page: 1 });
      } else {
        setPage(1);
      }
    }

    setPublishDrawerOpen(false);
    setPublishJobId(null);
    setPublishJobSnapshot(null);
  };

  const handlePublishJobCreated = (job) => {
    setPublishJobId(job?.id || null);
    setPublishJobSnapshot(job || null);
    setPublishToastVisible(false);
    setPublishToast(null);
    setPublishDrawerOpen(true);
  };

  const handlePublishToastDetail = () => {
    setPublishDrawerOpen(true);
    setPublishToastVisible(false);
  };

  const handleBulkPriceButtonClick = () => {
    if (selectedRowsForPriceUpdate.length === 0) {
      setSnackbarVariant('info');
      setSnackbarMessage('Selecciona al menos una publicación para usar esta acción.');
      setShowMessage(true);
      return;
    }

    handleBulkPriceUpdateModalOpen();
  };

  const handleBulkPackButtonClick = () => {
    if (selectedListingRows.length === 0) {
      setSnackbarVariant('info');
      setSnackbarMessage('Selecciona al menos una publicación para configurar un pack.');
      setShowMessage(true);
      return;
    }
    if (packEligibleListings.length === 0) {
      setSnackbarVariant('info');
      setSnackbarMessage('Las publicaciones de Mercado Libre Full no permiten configurar packs.');
      setShowMessage(true);
      return;
    }

    setBulkPackModalOpen(true);
  };

  const handleConfigureKit = () => {
    if (selectedListingRows.length !== 1) {
      setSnackbarVariant('info');
      setSnackbarMessage('Selecciona una sola publicación para configurar un kit.');
      setShowMessage(true);
      return;
    }
    const selectedRow = selectedListingRows[0];
    const listing = selectedRow?.listings?.[0];
    if (!['mercado_libre', 'tienda_nube'].includes(listing?.marketplace)) {
      setSnackbarVariant('info');
      setSnackbarMessage('Los kits se configuran desde una publicación de Mercado Libre o Tienda Nube.');
      setShowMessage(true);
      return;
    }
    if (selectedRow.kind === 'KIT' || listing.kind === 'KIT') {
      setSnackbarVariant('info');
      setSnackbarMessage('Esta publicación ya está configurada como kit.');
      setShowMessage(true);
      return;
    }
    if (Number(listing.saleUnitQuantity || 1) > 1) {
      setSnackbarVariant('info');
      setSnackbarMessage('Esta publicación ya está configurada como pack y no se puede usar para un kit.');
      setShowMessage(true);
      return;
    }
    if (String(listing.stockMode || 'standard') === 'full_only') {
      setSnackbarVariant('info');
      setSnackbarMessage('Las publicaciones de Mercado Libre Full no se pueden configurar como kit.');
      setShowMessage(true);
      return;
    }
    setKitSourceListing({
      ...listing,
      sku: selectedRow.sku || listing.sku || '',
      title: selectedRow.title || listing.title || 'Publicación sin título',
      marketplaceId: selectedRow.marketplaceId || listing.marketplaceId || '',
    });
    setKitCreateWizardOpen(true);
  };

  const handleKitWizardClose = () => {
    setKitCreateWizardOpen(false);
    setKitSourceListing(null);
  };

  const handleKitCreated = (kit) => {
    setHasCreatedKit(true);
    setSelectedListingIds([]);
    setSelectedItems([]);
    void fetchItems();
    setSnackbarVariant('success');
    const associatedCount = Number(kit?.association?.associatedListingCount || 1);
    const skippedCount = Array.isArray(kit?.association?.skippedMatchingListings)
      ? kit.association.skippedMatchingListings.length
      : 0;
    setSnackbarMessage(
      kit?.association?.status === 'PUSH_FAILED'
        ? 'El kit quedó configurado, pero no pudimos actualizar su stock en Mercado Libre.'
        : kit?.association?.pushedStock === false
          ? 'El kit quedó configurado sin actualizar el stock publicado todavía.'
          : skippedCount > 0
            ? `El kit quedó configurado en ${associatedCount} publicaciones; ${skippedCount} no se pudieron asociar.`
          : associatedCount > 1
            ? `El kit quedó configurado en ${associatedCount} publicaciones con el mismo SKU.`
            : 'El kit quedó configurado y la publicación ya usa su stock calculado.',
    );
    setShowMessage(true);
  };

  const handleViewKit = (kitId) => {
    if (kitId) setKitDetailId(String(kitId));
  };

  const handleKitUndone = (result) => {
    setKitDetailId(null);
    setHasCreatedKit(false);
    setSelectedListingIds([]);
    setSelectedItems([]);
    void fetchItems();
    setSnackbarVariant(result?.status === 'PUSH_FAILED' ? 'warning' : 'success');
    setSnackbarMessage(
      result?.status === 'PUSH_FAILED'
        ? 'El kit se deshizo, pero no pudimos actualizar algunas publicaciones en el canal.'
        : `Kit deshecho. Se restauraron ${Number(result?.restoredListingCount || 0)} publicaciones.`,
    );
    setShowMessage(true);
  };

  const handlePackModalSaved = async ({ savedCount, failedCount, skippedCount }) => {
    await savePackModalResult(
      { savedCount, failedCount, skippedCount },
      {
        fetchItems,
        refreshStockInconsistencySummary,
        setFinancialRefreshNonce,
        setSnackbarVariant,
        setSnackbarMessage,
        setShowMessage,
      },
    );
  };

  const handleBulkPriceUpdateFormChange = (e, { name, value }) => {
    setBulkPriceUpdateFormData({ ...bulkPriceUpdateFormData, [name]: value });
  };

  const handleInlineListingUpdate = React.useCallback(async (listingId, payload) => {
    try {
      const response = await stockyApi.updateListingInline(listingId, payload, authUser);
      await fetchItems();
      if (payload?.stock !== undefined || payload?.saleUnitQuantity !== undefined) {
        void refreshStockInconsistencySummary({ silent: true });
      }
      // The pricing view detects the changed local input and refreshes its own stale snapshot.
      setFinancialRefreshNonce((value) => value + 1);
      setSnackbarVariant('success');
      setSnackbarMessage('Listing actualizado.');
      setShowMessage(true);
      return response;
    } catch (error) {
      console.error('Error updating listing inline:', error);
      if (isInlineConfirmationError(error)) {
        throw error;
      }
      setSnackbarVariant('error');
      setSnackbarMessage('Error actualizando listing.');
      setShowMessage(true);
      throw error;
    }
  }, [
    authUser,
    fetchItems,
    refreshStockInconsistencySummary,
  ]);

  const handleDeleteListing = React.useCallback(async (listingId, options = {}) => {
    try {
      await stockyApi.deleteListing(listingId, authUser);
      setSelectedListingIds((prev) =>
        (prev || []).filter((id) => String(id || '').trim() !== String(listingId || '').trim()),
      );
      if (options.refresh !== false) {
        await fetchItems();
      }
      if (options.notify !== false) {
        setSnackbarVariant('success');
        setSnackbarMessage('Listing borrado de MercuryCom.');
        setShowMessage(true);
      }
    } catch (error) {
      console.error('Error deleting listing:', error);
      if (options.notify !== false) {
        setSnackbarVariant('error');
        setSnackbarMessage('Error borrando el listing de MercuryCom.');
        setShowMessage(true);
      }
      throw error;
    }
  }, [authUser, fetchItems]);

  const handleBulkDeleteListings = async () => {
    if (selectedListingRows.length === 0) {
      setBulkDeleteModalOpen(false);
      return;
    }

    setBulkDeleteSaving(true);
    const listingIds = Array.from(
      new Set(selectedListingRows.map((listing) => String(listing.id || '').trim()).filter(Boolean)),
    );
    let deletedCount = 0;
    let failedCount = 0;

    try {
      for (const listingId of listingIds) {
        try {
          await handleDeleteListing(listingId, { refresh: false, notify: false });
          deletedCount += 1;
        } catch {
          failedCount += 1;
        }
      }

      await fetchItems();
      setSelectedItems([]);
      setSelectedListingIds([]);
      setBulkDeleteModalOpen(false);
      setSnackbarVariant(failedCount > 0 ? 'error' : 'success');
      setSnackbarMessage(
        failedCount > 0
          ? `Se borraron ${deletedCount} listings de MercuryCom y fallaron ${failedCount}.`
          : `Se borraron ${deletedCount} listings de MercuryCom.`,
      );
      setShowMessage(true);
    } finally {
      setBulkDeleteSaving(false);
    }
  };

  const handleBulkPriceUpdateFormSubmit = async (updates, selectedListings) => {
    try {
      setBulkPriceSaving(true);
      const response = await stockyApi.bulkUpdateListingsInline(updates, authUser);
      await fetchItems();
      setBulkPriceUpdateModalOpen(false);
      setSelectedItems([]);
      setSelectedListingIds([]);

      setSnackbarVariant('success');
      setSnackbarMessage('Precios actualizados.');
      setShowMessage(true);

      const selectedByListingId = new Map(
        (selectedListings || []).map((listing) => [listing.id, listing]),
      );
      const normalizedResults = (response?.data?.results || []).map((row) => {
        const source = selectedByListingId.get(row.listingId);
        return {
          title: source?.title || source?.parentTitle || row.marketplaceId || row.listingId,
          marketplace: row.marketplace || source?.marketplace || '',
          status: row.status,
          priceBefore:
            row.priceBefore !== undefined ? row.priceBefore : Number(source?.price),
          priceAfter:
            row.priceAfter !== undefined ? row.priceAfter : undefined,
          error: row.error || '',
        };
      });
      setUpdateResults(normalizedResults);
      setShowResultsModal(true);
    } catch (error) {
      console.error('Error updating listings:', error);
      setSnackbarVariant('error');
      setSnackbarMessage('Error actualizando precios masivos.');
      setShowMessage(true);
    } finally {
      setBulkPriceSaving(false);
    }
  };

  const handleSort = React.useCallback((clickedColumn) => {
    if (column !== clickedColumn) {
      setColumn(clickedColumn);
      const sorted = [...items].sort((a, b) => {
        let aVal = a[clickedColumn];
        let bVal = b[clickedColumn];

        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';

        if (clickedColumn === 'price' || clickedColumn === 'cost' || clickedColumn === 'availableQuantity') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }

        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      });
      setItems(sorted);
      setDirection('ascending');
    } else {
      setItems([...items].reverse());
      setDirection(direction === 'ascending' ? 'descending' : 'ascending');
    }
  }, [column, direction, items, setItems]);

  const handleCloseModal = () => {
    setShowResultsModal(false);
    setUpdateResults([]);
  };

  const handleImportCompleted = async (importProgress) => {
    setSnackbarVariant('success');
    setSnackbarMessage('Importacion finalizada.');
    setShowMessage(true);
    const nextItems = await fetchItems();
    const changedRows = (importProgress?.results || []).filter(
      (row) => row?.costStatus === 'UPDATED' || row?.priceStatus === 'UPDATED',
    );
    const changedListingIds = Array.from(
      new Set(
        changedRows
          .map((row) => String(row?.listingId || '').trim())
          .filter(Boolean),
      ),
    );
    const changedSkus = new Set(
      changedRows
        .map((row) => String(row?.sku || '').trim())
        .filter(Boolean),
    );
    const impactedMlListingIds = Array.from(
      new Set(
        (nextItems || []).flatMap((item) => {
          const itemSku = String(item?.sku || '').trim();
          if (changedSkus.size > 0 && !changedSkus.has(itemSku)) {
            return [];
          }
          if (item?.marketplace !== 'mercado_libre') {
            return [];
          }
          return [String(item?.listingId || '').trim()].filter(Boolean);
        }).concat(changedListingIds),
      ),
    );
    if (impactedMlListingIds.length > 0) {
      setFinancialRefreshNonce((value) => value + 1);
    }
  };

  const handleStockImportCompleted = async () => {
    setSnackbarVariant('success');
    setSnackbarMessage('Importacion de stock finalizada.');
    setShowMessage(true);
    await fetchItems();
  };
  const handleSkuMigrationImportCompleted = async () => {
    setSnackbarVariant('success');
    setSnackbarMessage('Migracion de SKU finalizada.');
    setShowMessage(true);
    await fetchItems();
  };

  const handleOpenFinancialDetail = React.useCallback((listing, financial) => {
    setSelectedFinancialDetail({
      listing,
      financial,
    });
  }, []);
  const handleRetryFinancials = React.useCallback(() => {
    setFinancialRefreshNonce((value) => value + 1);
  }, []);

  const handlePageSizeChange = React.useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, [setPage, setPageSize]);

  useEffect(() => {
    if (!publishJobId) {
      setPublishJobSnapshot(null);
      return undefined;
    }

    let alive = true;
    let timer = null;

    const refreshJob = async () => {
      try {
        const job = await getPublishJobStatus(publishJobId);
        if (!alive || !job) return;
        setPublishJobSnapshot(job);
        if (job.status === 'COMPLETED' || job.status === 'COMPLETED_WITH_ERRORS') {
          if (Number(job?.summary?.created || 0) > 0) {
            void refreshUserFeatureFlags();
          }
          if (publishToastJobId !== publishJobId) {
            const createdCount = Number(job?.summary?.created || 0);
            const errorCount = Number(job?.summary?.errors || 0);
            const reviewCount = Number(job?.summary?.reviewRequired || 0);
            const omittedCount = Number(job?.summary?.omitted || 0);
            const lines = ['MercuryCom continuó el proceso en segundo plano.'];
            if (createdCount > 0) {
              lines.push(`${createdCount} publicaciones creadas.`);
            }
            if (reviewCount > 0) {
              lines.push(`${reviewCount} publicaciones requieren revisión.`);
            }
            if (errorCount > 0) {
              lines.push(`${errorCount} publicaciones fallaron.`);
            }
            if (omittedCount > 0) {
              lines.push(`${omittedCount} publicaciones omitidas.`);
            }
            setPublishToast({
              title:
                reviewCount > 0
                  ? `${createdCount} publicaciones creadas y ${reviewCount} requieren revisión.`
                  : errorCount > 0
                    ? `${createdCount} publicaciones creadas y ${errorCount} con error.`
                    : `${createdCount || job.totalToCreate || 0} publicaciones enviadas.`,
              lines,
              actionLabel: 'Ver detalle',
            });
            setPublishToastVisible(true);
            setPublishToastJobId(publishJobId);
          }
          if (timer !== null) {
            window.clearInterval(timer);
          }
        }
      } catch (error) {
        console.error('Error refreshing publish job status:', error);
      }
    };

    refreshJob();
    timer = window.setInterval(refreshJob, 1000);
    return () => {
      alive = false;
      if (timer !== null) {
        window.clearInterval(timer);
      }
    };
  }, [publishJobId, publishToastJobId, refreshUserFeatureFlags]);

  useEffect(() => {
    if (!publishToastVisible || !publishToast) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setPublishToastVisible(false);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [publishToastVisible, publishToast]);

  const handleUserSyncToggle = async () => {
    const result = await toggleUserSync();
    if (result.success) {
      setSnackbarVariant('success');
      setSnackbarMessage(result.nextValue ? 'Sync de ventas reactivado.' : 'Sync de ventas pausado.');
      setShowMessage(true);
    } else {
      setSnackbarVariant('error');
      setSnackbarMessage('No se pudo actualizar el sync de ventas.');
      setShowMessage(true);
    }
  };

  const activeFilterCount = getActiveFilterCount(filters);

  useEffect(() => {
    if (!isPublicationGridRoute) {
      return;
    }

    const params = new URLSearchParams(location.search || '');
    const sku = String(params.get('sku') || '').trim();
    const sellerId = String(params.get('sellerId') || '').trim();
    const listingId = String(params.get('listingId') || '').trim();
    const search = String(params.get('search') || '').trim();
    const marketplace = String(params.get('marketplace') || '').trim();
    const status = String(params.get('status') || '').trim();
    const exclusiveToChannel = params.get('exclusiveToChannel') === 'true' && Boolean(marketplace);

    if (!sku && !sellerId && !listingId && !search && !marketplace && !status && !exclusiveToChannel) {
      setPendingFilters(initialFilters);
      setFilters(initialFilters);
      setPage(1);
      return;
    }

    const nextFilters = {
      ...initialFilters,
      sku,
      sellerId,
      search,
      marketplace,
      status,
      exclusiveToChannel,
    };

    setPendingFilters(nextFilters);
    setFilters(nextFilters);
    setPage(1);
  }, [isPublicationGridRoute, location.search, setFilters, setPage]);

  if (isLoading) {
    return (
      <div className="centered-loader-container">
        <Dimmer active inverted>
          <Loader inverted size="huge">
            Cargando...
          </Loader>
        </Dimmer>
      </div>
    );
  }

  return (
    <Container fluid style={homePageShellStyle}>
      {authUser ? (
        <Segment style={homePageFrameStyle}>
          <div style={homePageBodyStyle}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                alignItems: 'stretch',
                gap: 8,
                margin: '8px 8px 8px',
              }}
            >
              <GlobalProcesses
                isPricesRoute={isPricesRoute}
                bulkPriceUpdateEnabled={bulkPriceUpdateEnabled}
                skuMigrationImportEnabled={skuMigrationImportEnabled}
                onStockImport={handleBulkStockImportModalOpen}
                onSkuMigrationImport={handleSkuMigrationImportModalOpen}
                onPricingImport={handleBulkPricingImportModalOpen}
              />
              <SyncStatusPanel
                userSyncEnabled={userSyncEnabled}
                syncControlsLoading={syncControlsLoading}
                onUserSyncToggle={handleUserSyncToggle}
                canManage={canManageSync}
              />
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setFiltersExpanded((prev) => !prev)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setFiltersExpanded((prev) => !prev);
                }
              }}
              style={{
                ...sectionTitleStyle,
                fontSize: 18,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                marginLeft: 8,
                marginRight: 8,
                marginBottom: filtersExpanded ? 8 : 10,
              }}
            >
              <Icon name={filtersExpanded ? 'chevron up' : 'chevron down'} style={{ margin: 0, color: '#6B7280' }} />
              <span>Filtros</span>
              {activeFilterCount > 0 ? (
                <Label basic color="purple">
                  {activeFilterCount} activos
                </Label>
              ) : null}
            </div>
            {filtersExpanded ? (
            <Segment style={{ ...denseSurfaceStyle, margin: '0 8px 10px' }}>
            <Form onSubmit={handleFiltersSubmit}>
              <div className="home-filter-grid">
                  <div>
                    <Form.Field>
                      <label>Titulo</label>
                      <Input
                        icon="search"
                        placeholder="Buscar por titulo..."
                        value={pendingFilters.search}
                        onChange={(e) => setPendingFilters((prev) => ({ ...prev, search: e.target.value }))}
                      />
                    </Form.Field>
                  </div>
                  <div>
                    <Form.Field>
                      <label>SKU</label>
                      <Input
                        placeholder="SKU (min 3)"
                        value={pendingFilters.sku}
                        onChange={(e) => setPendingFilters((prev) => ({ ...prev, sku: e.target.value }))}
                      />
                    </Form.Field>
                  </div>
                  <div>
                    <Form.Field>
                      <label>Estado</label>
                      <Dropdown
                        selection
                        options={statusOptions}
                        value={getFilterSelectValue(pendingFilters.status)}
                        onChange={(_, data) =>
                          setPendingFilters((prev) => ({
                            ...prev,
                            status: normalizeFilterSelectValue(data.value),
                          }))
                        }
                      />
                    </Form.Field>
                  </div>
                  <div>
                    <Form.Field>
                      <label>Canal</label>
                      <Dropdown
                        selection
                        options={marketplaceOptions}
                        value={getFilterSelectValue(pendingFilters.marketplace)}
                        onChange={(_, data) => {
                          const marketplace = normalizeFilterSelectValue(data.value);
                          setPendingFilters((prev) => ({
                            ...prev,
                            marketplace,
                            exclusiveToChannel: marketplace ? prev.exclusiveToChannel : false,
                            sellerId: marketplace === prev.marketplace ? prev.sellerId : '',
                          }));
                        }}
                      />
                    </Form.Field>
                  </div>
                  <div>
                    <Form.Field className="exclusive-channel-filter-field">
                      <label>Disponibilidad en canales</label>
                      <div
                        className={`exclusive-channel-filter${
                          pendingFilters.exclusiveToChannel === true ? ' exclusive-channel-filter--active' : ''
                        }${!pendingFilters.marketplace ? ' exclusive-channel-filter--disabled' : ''}`}
                      >
                        <Checkbox
                          toggle
                          label={getExclusiveChannelShortLabel(pendingFilters.marketplace)}
                          checked={pendingFilters.exclusiveToChannel === true}
                          disabled={!pendingFilters.marketplace}
                          onChange={(_, data) =>
                            setPendingFilters((prev) => ({
                              ...prev,
                              exclusiveToChannel: Boolean(data.checked) && Boolean(prev.marketplace),
                            }))
                          }
                        />
                        <Popup
                          content={getExclusiveChannelHelp(pendingFilters.marketplace)}
                          position="top center"
                          trigger={
                            <button
                              type="button"
                              className="exclusive-channel-filter__help"
                              aria-label="Cómo funciona este filtro"
                            >
                              <Icon name="question circle outline" aria-hidden="true" />
                            </button>
                          }
                        />
                      </div>
                    </Form.Field>
                  </div>
                  <div>
                    <Form.Field>
                      <label>Seller</label>
                      <Dropdown
                        selection
                        search
                        clearable
                        options={sellerOptions}
                        loading={sellersLoading}
                        disabled={sellersLoading}
                        placeholder="Todos los sellers"
                        value={pendingFilters.sellerId}
                        onChange={(_, data) =>
                          setPendingFilters((prev) => ({ ...prev, sellerId: String(data.value || '') }))
                        }
                      />
                    </Form.Field>
                  </div>
              </div>
              <div className="home-filter-actions">
                <Button basic type="button" onClick={clearFilters}>
                  Limpiar
                </Button>
                <Button color="purple" type="submit">
                  Aplicar filtros
                </Button>
              </div>
              <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
            </Form>
            {(pendingFilters.sku || '').trim().length > 0 &&
            (pendingFilters.sku || '').trim().length < 3 ? (
              <div style={{ marginTop: 4, color: '#777' }}>
                SKU menor a 3 caracteres se ignora para no bloquear filtros.
              </div>
            ) : null}
            </Segment>
            ) : null}

            <div
              style={{
                ...sectionTitleStyle,
                fontSize: 18,
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                marginLeft: 8,
                marginRight: 8,
              }}
            >
              <span>Publicaciones</span>
              <Label basic>{total} resultados</Label>
            </div>
            <div style={{ padding: '0 4px 8px' }}>
              {itemsError ? (
                <Message negative>
                  No pudimos cargar las publicaciones. Intenta aplicar los filtros nuevamente.
                </Message>
              ) : null}
              <BulkActionsBar
                selectedCount={selectedListingRows.length}
                canPublishInAnotherAccount={canPublishInAnotherAccount}
                packsEnabled={packsEnabled}
                kitsEnabled={kitsEnabled}
                hasConfiguredKit={hasConfiguredKit}
                bulkPriceUpdateEnabled={bulkPriceUpdateEnabled}
                bulkPriceSaving={bulkPriceSaving}
                bulkDeleteSaving={bulkDeleteSaving}
                publishInAnotherAccountCopiesUsed={publishInAnotherAccountCopiesUsed}
                publishInAnotherAccountFreeLimit={publishInAnotherAccountFreeLimit}
                showFirstCopyPill={showFirstCopyCallout}
                onPublish={handlePublishDrawerOpen}
                onFirstCopyStart={handleFirstCopyStart}
                onPriceUpdate={handleBulkPriceButtonClick}
                onConfigurePack={handleBulkPackButtonClick}
                onConfigureKit={handleConfigureKit}
                onDelete={handleBulkDeleteButtonClick}
                onClearSelection={handleClearSelection}
              />
              <div
                ref={publicationSelectionRef}
                tabIndex={-1}
                aria-label="Selección de publicaciones para copiar"
                style={{ position: 'relative' }}
              >
                <ItemsTable
                  column={column}
                  direction={direction}
                  handleSort={handleSort}
                  handleSelectAll={handleSelectAll}
                  selectAll={allVisibleListingsSelected}
                  selectAllIndeterminate={someVisibleListingsSelected}
                  TableContentComponent={TableContentComponent}
                  filteredItems={filteredItems}
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                  selectedListingIds={selectedListingIds}
                  setSelectedListingIds={setSelectedListingIds}
                  publishSelectionMode={publishSelectionMode}
                  firstCopyGuidanceActive={firstCopyGuidanceActive}
                  onFirstCopyGuidanceDismiss={handleFirstCopyGuidanceDismiss}
                  autoExpandListingIds={copiedSourceListingIds}
                  handleEditClick={handleEditClick}
                  handleViewClick={handleViewClick}
                  rowEditEnabled={rowEditEnabled}
                  handleInlineListingUpdate={handleInlineListingUpdate}
                  handleDeleteListing={handleDeleteListing}
                  listingFinancialsById={listingFinancialsById}
                  listingFinancialsLoadingById={listingFinancialsLoadingById}
                  handleViewFinancialDetail={handleOpenFinancialDetail}
                  handleRetryFinancials={handleRetryFinancials}
                  onViewKit={handleViewKit}
                  bulkPriceUpdateEnabled={bulkPriceUpdateEnabled && isPricesRoute}
                  packsEnabled={packsEnabled}
                  serverPagination
                  serverPage={page}
                  serverTotalPages={totalPages}
                  serverTotalItems={total}
                  serverPageSize={pageSize}
                  onServerPageChange={setPage}
                  onServerPageSizeChange={handlePageSizeChange}
                  emptyMessage={
                    filters.exclusiveToChannel === true
                      ? 'No hay publicaciones exclusivas de este canal con estos filtros.'
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
        </Segment>
      ) : null}

      <BulkPriceUpdateModal
        open={bulkPriceUpdateModalOpen}
        onClose={handleBulkPriceUpdateModalClose}
        formData={bulkPriceUpdateFormData}
        handleFormChange={handleBulkPriceUpdateFormChange}
        handleFormSubmit={handleBulkPriceUpdateFormSubmit}
        selectedRows={selectedRowsForPriceUpdate}
        saving={bulkPriceSaving}
      />
      <BulkPricingImportWizardModal
        open={bulkPricingImportModalOpen}
        onClose={handleBulkPricingImportModalClose}
        authUser={authUser}
        onCompleted={handleImportCompleted}
      />
      <BulkStockImportWizardModal
        open={bulkStockImportModalOpen}
        onClose={handleBulkStockImportModalClose}
        authUser={authUser}
        onCompleted={handleStockImportCompleted}
      />
      <SkuMigrationImportModal
        open={skuMigrationImportModalOpen}
        onClose={handleSkuMigrationImportModalClose}
        authUser={authUser}
        onCompleted={handleSkuMigrationImportCompleted}
      />
      <ShowResultUpdateModal open={showResultsModal} results={updateResults} onClose={() => handleCloseModal()} />

      <PackConfigModal
        open={bulkPackModalOpen}
        listings={selectedPackListings}
        onClose={() => setBulkPackModalOpen(false)}
        onSaved={handlePackModalSaved}
        updateListing={(listingId, payload) => stockyApi.updateListingInline(listingId, payload, authUser)}
      />
      <KitCreateWizard
        open={kitCreateWizardOpen}
        onClose={handleKitWizardClose}
        authUser={authUser}
        onCreated={handleKitCreated}
        sourceListing={kitSourceListing}
      />
      <KitDetailModal
        open={Boolean(kitDetailId)}
        kitId={kitDetailId}
        authUser={authUser}
        onClose={() => setKitDetailId(null)}
        onUndone={handleKitUndone}
      />

      <ViewItemModal open={viewModalOpen} onClose={() => setViewModalOpen(false)} item={selectedItem} />
      <ListingFinancialDetailModal
        detail={selectedFinancialDetail}
        onClose={() => setSelectedFinancialDetail(null)}
      />
      <PublishInAnotherAccountDrawer
        open={publishDrawerOpen}
        onClose={handlePublishDrawerClose}
        authUser={authUser}
        selectedRows={selectedPublishRows}
        resumeJobId={publishJobId}
        jobSnapshot={publishJobSnapshot}
        onJobCreated={handlePublishJobCreated}
        onJobSnapshotChange={setPublishJobSnapshot}
        publishInAnotherAccountEnabled={publishInAnotherAccountEnabled}
        publishInAnotherAccountCopiesUsed={publishInAnotherAccountCopiesUsed}
        publishInAnotherAccountFreeLimit={publishInAnotherAccountFreeLimit}
        preloadedDestinationAccounts={firstCopyDestinationAccounts}
        preloadedDestinationAccountsLoading={firstCopyDestinationAccountsLoading}
      />
      <Modal open={bulkDeleteModalOpen} size="small" closeOnDimmerClick={false}>
        <Modal.Header>Borrar listings de MercuryCom</Modal.Header>
        <Modal.Content>
          <div style={{ display: 'grid', gap: 12 }}>
            <p style={{ margin: 0 }}>
              Se van a borrar de MercuryCom {selectedListingRows.length} listings y no de {selectedDeleteChannelsLabel}.
            </p>
            <Message
              warning
              style={{ margin: 0 }}
              content="MercuryCom dejará de gestionarlos y no los va a incluir en actualizaciones de stock ni inconsistencias futuras."
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setBulkDeleteModalOpen(false)}
            disabled={bulkDeleteSaving}
          >
            Cancelar
          </Button>
          <Button
            color="red"
            icon
            labelPosition="left"
            onClick={handleBulkDeleteListings}
            loading={bulkDeleteSaving}
            disabled={bulkDeleteSaving}
          >
            <Icon name="trash" />
            Borrar
          </Button>
        </Modal.Actions>
      </Modal>
      {publishToastVisible && publishToast ? (
        <div
          style={{
            position: 'fixed',
            right: 20,
            top: 80,
            zIndex: 1300,
            width: 'min(420px, calc(100vw - 32px))',
          }}
        >
          <Message
            positive
            onDismiss={() => setPublishToastVisible(false)}
            style={{ boxShadow: '0 18px 50px rgba(15, 23, 42, 0.18)' }}
          >
            <Message.Header>{publishToast.title}</Message.Header>
            {(publishToast.lines || []).map((line, index) => (
              <p key={`publish-toast-${index}`}>{line}</p>
            ))}
            {publishToast.actionLabel ? (
              <div style={{ marginTop: 10 }}>
                <Button size="tiny" color="violet" onClick={handlePublishToastDetail}>
                  {publishToast.actionLabel}
                </Button>
              </div>
            ) : null}
          </Message>
        </div>
      ) : null}

      <SemanticUiReactSnackbar variant={snackbarVariant} visible={showMessage} setVisible={setShowMessage}>
        {snackbarMessage}
      </SemanticUiReactSnackbar>
    </Container>
  );
}

export default Home;

const ListingFinancialDetailModal = ({ detail, onClose }) => {
  const financial = detail?.financial;
  const listing = detail?.listing;

  return (
    <Modal open={!!detail} onClose={onClose} size="small" closeIcon>
      <Modal.Header>Detalle financiero estimado</Modal.Header>
      <Modal.Content>
        {!financial ? (
          <Message info content="Sin datos financieros para mostrar." />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700 }}>{listing?.title || 'Publicacion'}</div>
              <div style={{ color: '#666', marginTop: 4 }}>
                {listing?.sellerNickname || '-'} · {listing?.marketplace || '-'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {financial.source ? <Label basic>{financial.source}</Label> : null}
              {financial.freshness === 'stale' ? <Label basic color="orange">Estimación guardada pendiente de actualización</Label> : null}
            </div>
            {financial.message ? <Message warning content={financial.message} /> : null}
            <Table basic="very" compact>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>Precio de venta</Table.Cell>
                  <Table.Cell textAlign="right">{formatFinancialCurrency(financial.salePriceAmount)}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Cargo por vender</Table.Cell>
                  <Table.Cell textAlign="right">-{formatFinancialCurrency(financial.marketplaceFeeAmount)}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Costo por cuotas</Table.Cell>
                  <Table.Cell textAlign="right">-{formatFinancialCurrency(financial.financingFeeAmount)}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Costo de envio ML</Table.Cell>
                  <Table.Cell textAlign="right">-{formatFinancialCurrency(financial.shippingCostAmount)}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Impuestos estimados</Table.Cell>
                  <Table.Cell textAlign="right">
                    {financial.taxEstimateAmount === null
                      ? 'No disponible'
                      : `-${formatFinancialCurrency(financial.taxEstimateAmount)}`}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Recibis</Table.Cell>
                  <Table.Cell textAlign="right" style={{ fontWeight: 700 }}>
                    {formatFinancialCurrency(financial.receivedAmount)}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Costo interno</Table.Cell>
                  <Table.Cell textAlign="right">
                    {hasInternalCostLoaded(financial)
                      ? `-${formatFinancialCurrency(financial.costAmount)}`
                      : 'No cargado'}
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Margen</Table.Cell>
                  <Table.Cell
                    textAlign="right"
                    style={{
                      fontWeight: 700,
                      color:
                        hasInternalCostLoaded(financial) && Number(financial.marginAmount || 0) < 0
                          ? '#a33333'
                          : '#1b7f3b',
                    }}
                  >
                    {renderMarginText(financial)}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
            <div style={{ color: '#666' }}>
              Actualizado: {formatDateTime(financial.fetchedAt)} · Vence: {formatDateTime(financial.expiresAt)}
            </div>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cerrar</Button>
      </Modal.Actions>
    </Modal>
  );
};

